import React from 'react';
/**
 * Returns true if an argument is a React class component, false otherwise.
 * @param component
 */
export function isClassComponent(component) {
    return typeof component === 'function' && !!component.prototype.isReactComponent;
}
/**
 * Returns true if an argument is a function component, false otherwise
 * TODO: it would be nice to use a type guard for this.
 * @see https://stackoverflow.com/questions/33199959/how-to-detect-a-react-component-vs-a-react-element
 * @param component
 */
export function isFunctionComponent(component) {
    return (React.isValidElement(component) ||
        (typeof component === 'function' &&
            String(component).includes('react') &&
            String(component).includes('createElement')));
}
/**
 * Returns true if the given value is a React component, false otherwise.
 * @param valueToCheck
 */
export const isReactComponent = (valueToCheck) => {
    // Check if this is a JSX element or not
    if (!(isClassComponent(valueToCheck) || isFunctionComponent(valueToCheck))) {
        return false;
    }
    // Basic HTML elements like <span> or <p> have a 'type' field of string type.
    // React components have a 'type' field that is a function.
    return typeof valueToCheck.type !== 'string';
};
/**
 * Returns true if the given value is a valid React element; false otherwise.
 * shortcut for React.isValidElement.
 * @param valueToCheck
 */
export const isElement = (element) => {
    return React.isValidElement(element);
};
/**
 * Given a context that provides a certain type of data, returns a HOC that can be used to wrap component classes
 * and return new components wrapped in a consumer for the given context.
 *
 * For example, a context can be constructed for some interface like StudentInfo:
 *
 *      const StudentInfoContext = React.createContext<StudentInfo | null>(null);
 *
 * We then construct an HOC using withContext, and the context we have created:
 *
 *      export const withStudentInfo = withContext(StudentInfoContext);
 *
 * Now, for some arbitrary component, we can have it consume data from the StudentInfoContext's provider by wrapping
 * its export in the withStudentInfo HOC:
 *
 *      export default withStudentInfo(SomeComponent)
 *
 * In addition to its usual props, SomeComponent will be passed additional props of type StudentInfo | null
 *
 * @param context The context to create an HOC for
 *
 * Type Params:
 *      T: the type of the data provided by the context. Inferred from the given parameter.
 *      P: the prop type of a component to be wrapped in the HOC. Inferred from the component passed to the HOC.
 */
export const withContext = (context) => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return (Component) => {
        const { Consumer } = context;
        /**
         * Here we return a new component that takes its original props (P) as well as the props provided by the context (T)
         */
        // eslint-disable-next-line react/display-name,react/prefer-stateless-function
        return class extends React.Component {
            /**
             * Wrap the component passed to the HOC in a consumer and inject both the original and provided props.
             */
            render() {
                return React.createElement(Consumer, null, providedData => React.createElement(Component, Object.assign({}, providedData, this.props)));
            }
        };
    };
};
const NO_OF_RETRIES = 20;
const INTERVAL = 250;
/**
 * A function that retries calls to get chunks from failed to get lazy components so the app can continue loading.
 * Example usage:
 * const LoadingComponent = React.lazy(() => ComponentLoader.componentLoader(() => import('./loadingComponent')) as Promise<any>);
 * @param lazyComponent
 * @param attemptsLeft
 * @see https://medium.com/@botfather/react-loading-chunk-failed-error-88d0bb75b406
 */
export const componentLoader = (lazyComponent, attemptsLeft = NO_OF_RETRIES) => {
    return new Promise((resolve, reject) => {
        lazyComponent()
            .then(resolve)
            .catch(error => {
            setTimeout(() => {
                if (attemptsLeft <= 1) {
                    reject(error);
                    return;
                }
                componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject);
            }, INTERVAL);
        });
    });
};
//# sourceMappingURL=index.js.map